/**
 * 期間限定ページ: 2025年夏の無料サウンド素材配布キャンペーン
 *
 * - メンテ寿命が短い LP のため、UI 土台ルール（docs/ui-foundation/04-page-rules.md）の
 *   禁則 A〜C（独自 styled / utility 未使用 / !important 上書き）の制約を緩めています。
 * - 通常ページの規約とは別扱いです。リファクタ対象に含めないでください。
 * - キャンペーン終了後は、ディレクトリごと削除する想定です。
 */
import React, { useState, useEffect, useRef, useMemo, memo, useCallback } from "react"
import styled, { keyframes, createGlobalStyle } from "styled-components"
import Button from "../../components/button/Button"
import Tippy from "@tippyjs/react"
import "../../components/tooltip/tooltip_tippy.css"
import MicroInteraction from "../../components/micro-interaction/MicroInteraction"

// Background video
import BackgroundVideo from "../../assets/videos/free-assets/2025-summer_optimized.mp4"

// Audio imports (static imports like Os.jsx)
import AquaOrbit from "../../assets/audio/free-sound-assets/2025-summer/Aqua Orbit.mp3"
import AuroraMidasShine from "../../assets/audio/free-sound-assets/2025-summer/Aurora Midas Shine.mp3"
import HeatCometCharge from "../../assets/audio/free-sound-assets/2025-summer/Heat Comet Charge.mp3"
import IgnitionStart from "../../assets/audio/free-sound-assets/2025-summer/Ignition Start.mp3"
import MadnessUniverse from "../../assets/audio/free-sound-assets/2025-summer/Madness Universe.mp3"
import NebulaHeartFlash from "../../assets/audio/free-sound-assets/2025-summer/Nebula Heart Flash.mp3"
import SevenColorNova from "../../assets/audio/free-sound-assets/2025-summer/Seven-Color Nova.mp3"
import SproutNebula from "../../assets/audio/free-sound-assets/2025-summer/Sprout Nebula.mp3"
import StarbunnyDeploy from "../../assets/audio/free-sound-assets/2025-summer/Starbunny Deploy.mp3"
import StellaSeed from "../../assets/audio/free-sound-assets/2025-summer/Stella Seed.mp3"
import CosmicKissHit from "../../assets/audio/free-sound-assets/2025-summer/Cosmic Kiss Hit.mp3"
import GalaxyTreasureRush from "../../assets/audio/free-sound-assets/2025-summer/Galaxy Treasure Rush.mp3"
import GoldChanceHit from "../../assets/audio/free-sound-assets/2025-summer/Gold Chance Hit.mp3"
import StellarSweetZone from "../../assets/audio/free-sound-assets/2025-summer/Stellar Sweet Zone.mp3"
import SugarStarlightGlitch from "../../assets/audio/free-sound-assets/2025-summer/Sugar Starlight Glitch.mp3"

// Map audio files
const audioModules: Record<string, string> = {
  "Aqua Orbit.mp3": AquaOrbit,
  "Aurora Midas Shine.mp3": AuroraMidasShine,
  "Heat Comet Charge.mp3": HeatCometCharge,
  "Ignition Start.mp3": IgnitionStart,
  "Madness Universe.mp3": MadnessUniverse,
  "Nebula Heart Flash.mp3": NebulaHeartFlash,
  "Seven-Color Nova.mp3": SevenColorNova,
  "Sprout Nebula.mp3": SproutNebula,
  "Starbunny Deploy.mp3": StarbunnyDeploy,
  "Stella Seed.mp3": StellaSeed,
  "Cosmic Kiss Hit.mp3": CosmicKissHit,
  "Galaxy Treasure Rush.mp3": GalaxyTreasureRush,
  "Gold Chance Hit.mp3": GoldChanceHit,
  "Stellar Sweet Zone.mp3": StellarSweetZone,
  "Sugar Starlight Glitch.mp3": SugarStarlightGlitch
}

// Custom tooltip styles for this page
const CustomTooltipStyles = createGlobalStyle`
  .tippy-box[data-theme~='free-sound-assets'] {
    background: #fcfcfc !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    color: #000 !important;
    border: 1px solid #f0f0f0 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  }
  
  .tippy-box[data-theme~='free-sound-assets'] .tippy-arrow {
    color: #fcfcfc !important;
  }
`

// Custom ToolTip component for this page
interface CustomToolTipProps {
  description: string;
  placement?: "top" | "bottom" | "left" | "right";
  children: React.ReactElement;
}

const CustomToolTip = ({ description, placement = "top", children }: CustomToolTipProps) => {
  return (
    <Tippy
      content={description}
      arrow={false}
      delay={[0, 0]}
      duration={[0, 300]}
      placement={placement}
      offset={[0, 8]}
      theme="free-sound-assets"
      touch={false} // タッチデバイスでは無効化
      trigger="mouseenter" // ホバーのみでトリガー
    >
      {children}
    </Tippy>
  );
}

interface AudioItem {
  id: number
  title: string
  description: string
  gradient: string
  audioFileName: string
  frequency: number
}

const audioItems: AudioItem[] = [
  {
    id: 1,
    title: "Aqua Orbit",
    description: "水の軌道を描く宇宙保留音",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    audioFileName: "Aqua Orbit.mp3",
    frequency: 220,
  },
  {
    id: 2,
    title: "Aurora Midas Shine",
    description: "黄金のオーロラが輝く音",
    gradient: "linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)",
    audioFileName: "Aurora Midas Shine.mp3",
    frequency: 330,
  },
  {
    id: 3,
    title: "Heat Comet Charge",
    description: "熱を帯びた彗星のチャージ音",
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    audioFileName: "Heat Comet Charge.mp3",
    frequency: 440,
  },
  {
    id: 4,
    title: "Ignition Start",
    description: "パワフルな特化ゾーン導入シークエンス",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    audioFileName: "Ignition Start.mp3",
    frequency: 550,
  },
  {
    id: 5,
    title: "Madness Universe",
    description: "混沌とした宇宙の上位AT",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    audioFileName: "Madness Universe.mp3",
    frequency: 660,
  },
  {
    id: 6,
    title: "Nebula Heart Flash",
    description: "星雲の鼓動が閃く一発告知",
    gradient: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
    audioFileName: "Nebula Heart Flash.mp3",
    frequency: 770,
  },
  {
    id: 7,
    title: "Seven-Color Nova",
    description: "七色に輝く超新星",
    gradient: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    audioFileName: "Seven-Color Nova.mp3",
    frequency: 880,
  },
  {
    id: 8,
    title: "Sprout Nebula",
    description: "芽生える星雲のエネルギー",
    gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    audioFileName: "Sprout Nebula.mp3",
    frequency: 990,
  },
  {
    id: 9,
    title: "Starbunny Deploy",
    description: "かわいい星うさぎが展開",
    gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    audioFileName: "Starbunny Deploy.mp3",
    frequency: 1100,
  },
  {
    id: 10,
    title: "Stella Seed",
    description: "星の種が芽吹く音",
    gradient: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    audioFileName: "Stella Seed.mp3",
    frequency: 1210,
  },
  {
    id: 11,
    title: "Cosmic Kiss Hit",
    description: "宇宙からのキスが響く確定音",
    gradient: "linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)",
    audioFileName: "Cosmic Kiss Hit.mp3",
    frequency: 1320,
  },
  {
    id: 12,
    title: "Galaxy Treasure Rush",
    description: "銀河の宝物を獲得",
    gradient: "linear-gradient(135deg, #48c6ef 0%, #6f86d6 100%)",
    audioFileName: "Galaxy Treasure Rush.mp3",
    frequency: 1430,
  },
  {
    id: 13,
    title: "Gold Chance Hit",
    description: "黄金のチャンスがカットイン",
    gradient: "linear-gradient(135deg, #f7b733 0%, #fc4a1a 100%)",
    audioFileName: "Gold Chance Hit.mp3",
    frequency: 1540,
  },
  {
    id: 14,
    title: "Stellar Sweet Zone",
    description: "恒星の甘美なゾーン開始",
    gradient: "linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)",
    audioFileName: "Stellar Sweet Zone.mp3",
    frequency: 1650,
  },
  {
    id: 15,
    title: "Sugar Starlight Glitch",
    description: "砂糖のような星明かりのショートフリーズ",
    gradient: "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
    audioFileName: "Sugar Starlight Glitch.mp3",
    frequency: 1760,
  },
]

// CSS Animation for rotation
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

// Flash animation for click effect
const flashAnimation = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
    filter: blur(0px);
  }
  50% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.8;
    filter: blur(2px);
  }
  100% {
    transform: translate(-50%, -50%) scale(3);
    opacity: 0;
    filter: blur(20px);
  }
`

const Container = styled.div.attrs({
  className: "u-row-center"
})`
  min-height: 100vh;
  overflow: hidden;
  padding: 64px 16px 144px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  isolation: isolate;
  background-color: #000000;
`

const BackgroundVideoElement = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  min-width: 100%;
  min-height: 100%;
  object-fit: cover; /* 動画を画面全体にフィットさせる */
  z-index: -1;
`

const CircularContainer = styled.div.attrs({
  className: "u-relative"
})`
  aspect-ratio: 1;
  width: min(calc(100vw - 32px), calc(100vh - 32px));
  height: min(calc(100vw - 32px), calc(100vh - 32px));
  position: relative;
  z-index: 1;
  animation: ${rotate} 120s linear infinite;
  transform: translateZ(0); /* GPU アクセラレーション有効化 */
  will-change: transform; /* アニメーション最適化のヒント */
  transform-style: preserve-3d; /* 3D変換の最適化 */
  backface-visibility: hidden; /* 裏面の非表示で最適化 */
`

interface CircleButtonProps {
  $containerSize: number
  $x: number
  $y: number
  $gradient: string
}

const CircleButton = styled.button.attrs<CircleButtonProps>({
  className: "u-absolute"
})<CircleButtonProps>`
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.25);
  /* backdrop-filterをより軽いブラー効果に変更 */
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  overflow: hidden;
  position: absolute;
  
  /* モバイルでのタップ時の選択状態を防ぐ */
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  touch-action: manipulation;
  will-change: transform; /* ホバー時の変形を最適化 */
  contain: layout style paint; /* CSS containmentで最適化 */
  
  width: ${(props) => props.$containerSize * 0.08}px;
  height: ${(props) => props.$containerSize * 0.08}px;
  left: calc(50% + ${(props) => props.$x}px - ${(props) => props.$containerSize * 0.04}px);
  top: calc(50% + ${(props) => props.$y}px - ${(props) => props.$containerSize * 0.04}px);
  
  /* ガラスのような多層グラデーション背景 */
  background: 
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 50%,
      rgba(255, 255, 255, 0.025) 100%
    ),
    ${(props) => props.$gradient};
  
  /* 簡略化されたシャドウでパフォーマンス向上 */
  box-shadow: 
    0 0 20px rgba(255, 255, 255, 0.2),
    0 4px 16px rgba(0, 0, 0, 0.2);
  
  /* ガラスの光沢を表現する疑似要素 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 30% 30%,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.1) 30%,
      transparent 70%
    );
    opacity: 0.8;
    transition: opacity 0.3s ease;
  }
  
  /* 下部の反射光 */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(255, 255, 255, 0.05) 50%,
      rgba(255, 255, 255, 0.1) 100%
    );
    opacity: 0.6;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: scale(1.12);
    border-color: rgba(255, 255, 255, 0.4);
    
    /* 簡略化されたホバー効果 */
    box-shadow: 
      0 0 30px rgba(255, 255, 255, 0.4),
      0 8px 24px rgba(0, 0, 0, 0.25);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      opacity: 0.8;
    }
  }

  &:active {
    transform: scale(1.05);
    box-shadow: 
      0 0 20px rgba(255, 255, 255, 0.3),
      0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &:focus {
    outline: none;
    box-shadow: 
      0 0 0 3px rgba(255, 255, 255, 0.4),
      0 0 30px rgba(255, 255, 255, 0.4);
  }
`

interface CenterDotProps {
  $containerSize: number
}

const CenterDot = styled.div.attrs<CenterDotProps>({
  className: "u-absolute"
})<CenterDotProps>`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 50%;
  opacity: 0.6;
  width: ${(props) => props.$containerSize * 0.0075}px;
  height: ${(props) => props.$containerSize * 0.0075}px;
  box-shadow: 0 0 ${(props) => props.$containerSize * 0.0375}px rgba(255,255,255,0.8);
`

// Flash effect component
interface FlashEffectProps {
  id: string
  x: number
  y: number
  gradient: string
}

const FlashEffect = styled.div<FlashEffectProps>`
  position: absolute;
  width: 120px;
  height: 120px;
  left: calc(50% + ${props => props.x}px);
  top: calc(50% + ${props => props.y}px);
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 100;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    background: ${props => props.gradient};
    border-radius: 50%;
    animation: ${flashAnimation} 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    box-shadow: 
      0 0 40px rgba(255, 255, 255, 0.8),
      0 0 80px rgba(255, 255, 255, 0.6),
      0 0 120px rgba(255, 255, 255, 0.4);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255,255,255,0.9) 0%, transparent 70%);
    border-radius: 50%;
    animation: ${flashAnimation} 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }
`

const ToastContainer = styled.div`
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
`

// CC0 License notice container
const LicenseContainer = styled.div`
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 10;
  max-width: 756px;
  
  @media (max-width: 640px) {
    top: 8px;
    right: 8px;
    left: 8px;
    max-width: none;
  }
`

const LicenseContent = styled.p.attrs({
  className: "c-group u-row-center u-gap4 bodytext-11"
})`
  flex-wrap: wrap;
  margin: 0;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  
  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: white;
      text-decoration: underline;
    }
  }
  
  img {
    height: 16px !important;
    margin-left: 4px;
    vertical-align: middle;
    opacity: 0.8;
  }
`

const ToastContent = styled.div`
  // border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 16px 12px;
  width: clamp(224px, 100vw - 32px, 756px);
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 16px;

  /* From https://css.glass */
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
`

interface ToastIconProps {
  $gradient: string
}

const ToastIcon = styled.div<ToastIconProps>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: ${(props) => props.$gradient};
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
`

const ToastTextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const ToastTitle = styled.h3.attrs({
  className: "heading-16"
})`
  color: white;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ToastDescription = styled.p.attrs({
  className: "bodytext-13"
})`
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DownloadButtonWrapper = styled.div`
  flex-shrink: 0;
  
  button {
    background: rgba(255, 255, 255, 0.15) !important;
    color: white !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    padding: 8px !important;
    min-width: auto !important;
    width: 40px !important;
    height: 40px !important;
    border-radius: 50% !important;

    &:hover {
      background: var(--soremo-blue) !important;
    }
  }
`

// Toast Component (Memoized)
interface AudioToastProps {
  item: AudioItem
  onDownload: () => void
}

const AudioToast = memo(({ item, onDownload }: AudioToastProps) => (
  <ToastContainer>
    <ToastContent>
      <ToastIcon $gradient={item.gradient} />
      <ToastTextContainer>
        <ToastTitle>{item.title}</ToastTitle>
        <ToastDescription>{item.description}</ToastDescription>
      </ToastTextContainer>
      <DownloadButtonWrapper>
        <CustomToolTip description="ダウンロード" placement="top">
          <span style={{ display: 'inline-block' }}>
            <MicroInteraction
              trigger={
                <Button
                  variant="filled"
                  theme="primary"
                  leftIcon="download"
                  onClick={onDownload}
                />
              }
              text="ありがとう！"
              placement="top"
              offset={12}
              duration={1500}
              fontSize="11px"
              color="#ffffff"
              backgroundColor="#009ACE"
              animationType="wobble"
            />
          </span>
        </CustomToolTip>
      </DownloadButtonWrapper>
    </ToastContent>
  </ToastContainer>
))

AudioToast.displayName = 'AudioToast'

interface FlashEffectItem {
  id: string
  x: number
  y: number
  gradient: string
}

// CircleButton component (Memoized)
const MemoizedCircleButton = memo(CircleButton)

// Audio cache for preloaded audio files
const audioCache = new Map<string, string>()

export default function CircularAudioApp() {
  const [selectedItem, setSelectedItem] = useState<AudioItem | null>(null)
  const [flashEffects, setFlashEffects] = useState<FlashEffectItem[]>([])
  const audioRef = useRef<HTMLAudioElement | null>(null)

  
  // Container size calculation with resize handling
  const [containerSize, setContainerSize] = useState(() => {
    if (typeof window === "undefined") return 800
    const availableHeight = window.innerHeight - 192 - 64  // bottom margin 192px + top padding 64px
    const availableWidth = window.innerWidth - 32  // left/right padding
    return Math.min(availableWidth, availableHeight)
  })

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const availableHeight = window.innerHeight - 192 - 64
      const availableWidth = window.innerWidth - 32
      setContainerSize(Math.min(availableWidth, availableHeight))
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])



  // Preload background video and audio files
  useEffect(() => {
    // Preload background video (handled by video element itself)
    
    // Preload audio files in the background
    const preloadAudioFiles = () => {
      for (const item of audioItems) {
        if (!audioCache.has(item.audioFileName)) {
          const audioUrl = audioModules[item.audioFileName]
          if (audioUrl) {
            audioCache.set(item.audioFileName, audioUrl)
          } else {
            console.error(`Failed to preload ${item.audioFileName}: Not found in audioModules`)
          }
        }
      }
    }
    
    // Start preloading after a short delay to prioritize initial render
    const preloadTimer = setTimeout(preloadAudioFiles, 1000)
    
    return () => clearTimeout(preloadTimer)
  }, [])

  useEffect(() => {
    if (selectedItem) {
      const timer = setTimeout(() => {
        setSelectedItem(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [selectedItem])

  // コンポーネントのアンマウント時に音声を停止
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])



  const playAudio = useCallback((item: AudioItem) => {
    // 非同期処理を分離して即座にUIフィードバックを提供
    // 既存の音声を停止
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }

    // 音声再生を非同期で処理
    const playAudioAsync = async () => {
      let audioUrl: string | undefined
      try {
        // キャッシュから音声URLを取得、なければマッピングから取得
        audioUrl = audioCache.get(item.audioFileName)
        if (!audioUrl) {
          // ファイル名でマッピングから取得
          audioUrl = audioModules[item.audioFileName]
          
          if (audioUrl) {
            audioCache.set(item.audioFileName, audioUrl)
          } else {
            console.error(`Audio file not found: ${item.audioFileName}`)
            return
          }
        }
        
        if (audioUrl) {
          const audio = new Audio(audioUrl)
          audioRef.current = audio
          
          // ブラウザの自動再生ポリシーに対応
          try {
            await audio.play()
          } catch (playError) {
            if (playError instanceof DOMException && playError.name === 'NotAllowedError') {
              console.warn('Audio playback was prevented by browser autoplay policy. User interaction required.')
            } else {
              throw playError
            }
          }
        }
      } catch (error) {
        console.error('Audio playback failed:', error)
        console.error('Audio URL:', audioUrl)
        console.error('Available audio files:', Object.keys(audioModules))
      }
    }

    // 非同期で音声再生を実行
    playAudioAsync()
  }, [])

  const handleCircleClick = useCallback((item: AudioItem, x: number, y: number) => {
    // 閃光エフェクトを追加（xとyはCircleButtonと同じ座標系）
    const flashId = `flash-${Date.now()}-${Math.random()}`
    const newFlash: FlashEffectItem = {
      id: flashId,
      x: x,
      y: y,
      gradient: item.gradient
    }
    
    setFlashEffects(prev => [...prev, newFlash])
    
    // アニメーション終了後に削除
    setTimeout(() => {
      setFlashEffects(prev => prev.filter(flash => flash.id !== flashId))
    }, 600)
    
    playAudio(item)
    setSelectedItem(item)
  }, [playAudio])

  const downloadAudio = useCallback((item: AudioItem) => {
    // 即座にダウンロードを開始（非同期処理を避ける）
    const audioUrl = audioCache.get(item.audioFileName) || audioModules[item.audioFileName]
    
    if (audioUrl) {
      // URLをキャッシュに保存
      if (!audioCache.has(item.audioFileName)) {
        audioCache.set(item.audioFileName, audioUrl)
      }
      
      // 即座にダウンロードリンクを作成してクリック
      const link = document.createElement("a")
      link.href = audioUrl
      link.download = `${item.title.toLowerCase().replace(/\s+/g, "-")}.mp3`
      link.style.display = 'none'
      document.body.appendChild(link)
      
      // 次のフレームでクリックを実行
      requestAnimationFrame(() => {
        link.click()
        // クリーンアップを少し遅延
        setTimeout(() => {
          document.body.removeChild(link)
        }, 100)
      })
    } else {
      console.error(`Audio file not found for download: ${item.audioFileName}`)
    }
  }, [])

  // 円形配置の計算をメモ化
  const circlePositions = useMemo(() => {
    const radius = containerSize * 0.4375
    return audioItems.map((item, index) => {
      const angle = index * 24
      const x = Math.cos((angle * Math.PI) / 180) * radius
      const y = Math.sin((angle * Math.PI) / 180) * radius
      return { item, x, y }
    })
  }, [containerSize])

  return (
    <>
      <CustomTooltipStyles />
      <Container>
        <BackgroundVideoElement
          src={BackgroundVideo}
          autoPlay
          loop
          muted
          playsInline
        />
        <CircularContainer>
          {circlePositions.map(({ item, x, y }) => (
            <MemoizedCircleButton
              key={item.id}
              $containerSize={containerSize}
              $x={x}
              $y={y}
              $gradient={item.gradient}
              onClick={() => handleCircleClick(item, x, y)}
              aria-label={`Play ${item.title}`}
            />
          ))}

          {/* Flash effects */}
          {flashEffects.map(flash => (
            <FlashEffect
              key={flash.id}
              id={flash.id}
              x={flash.x}
              y={flash.y}
              gradient={flash.gradient}
            />
          ))}

          <CenterDot $containerSize={containerSize} />
        </CircularContainer>

        {selectedItem && (
          <AudioToast item={selectedItem} onDownload={() => downloadAudio(selectedItem)} />
        )}
        
        {/* CC0 License Notice */}
        <LicenseContainer>
          <LicenseContent>
            <span>2025 Summer Sound Collection</span> by{" "}
            <span>SOREMO</span> is marked with{" "}
            <a
              href="https://creativecommons.org/publicdomain/zero/1.0/?ref=chooser-v1"
              target="_blank"
              rel="license noopener noreferrer"
              className="bodytext-11"
            >
              CC0 1.0 Universal
              <img
                src="https://mirrors.creativecommons.org/presskit/icons/cc.svg?ref=chooser-v1"
                alt="CC"
              />
              <img
                src="https://mirrors.creativecommons.org/presskit/icons/zero.svg?ref=chooser-v1"
                alt="0"
              />
            </a>
          </LicenseContent>
        </LicenseContainer>
      </Container>
    </>
  )
}
